import { useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';

import { Typography, Button, Box } from '@mui/material';

import { useAddCampaignMutation, useGenerateMessageMutation, useUpdateCampaignMutation, useUpdateCampaignStatusMutation } from 'api/services/dataEndpoints';
import { useAppSelector } from 'store/hooks';
import type { CampaignType, CampaignRecipientType, CampaignChannelType } from 'types/campaignTypes';
import Input from 'components/Input';
import CustomTextField from 'components/CustomTextField/CustomTextField';
import CustomAutocomplete from 'components/CustomAutocomplete/CustomAutocomplete';
import CustomDatePicker from 'components/CustomDatePicker/CustomDatePicker';
import CustomTimePicker from 'components/CustomTimePicker/CustomTimePicker';
import { Phone } from '@mui/icons-material';
import ChannelToggle from './components/ChannelToggle/ChannelToggle';

import CampaignFormContainer from './CampaignForm.style';

type PropsType = {
  campaign?: CampaignType | null;
  recipientList: CampaignRecipientType[];
  onSubmit?: () => void;
  onCancel?: () => void;
} & React.ComponentPropsWithoutRef<'div'>;

const validationSchema = Yup.object({
  channel: Yup.array().min(1, 'Select at least one recipient')
    .required('Channel is required'),
  name: Yup.string().trim().required('Name is required'),
  subject: Yup.string().trim().required('Subject is required'),
  message: Yup.string().trim().required('Message is required'),
  recipients: Yup.array().min(1, 'Select at least one recipient')
    .required('Recipients are required'),
  scheduledAt: Yup.date().required('Date is required')
    .min(dayjs(), 'Date and time must be in the future'),
});

const CampaignForm: React.FC<PropsType> = ({
  campaign,
  recipientList,
  onCancel: handleCancel = () => null,
  onSubmit: handleSave = () => null,
}) => {
  const userId = useAppSelector((state) => state.userSlice.user.id);

  const [addCampaign, { isError, isSuccess }] = useAddCampaignMutation();
  const [updateCampaign] = useUpdateCampaignMutation();
  const [updateStatus] = useUpdateCampaignStatusMutation();
  const [generateMessage] = useGenerateMessageMutation();

  const formik = useFormik({
    initialValues: {
      channel: campaign?.channel?.split('_') || [],
      name: campaign?.name || '',
      subject: campaign?.subject || '',
      message: campaign?.message || '',
      recipients: campaign?.recipients || [],
      scheduledAt: campaign?.scheduledAt
        ? dayjs(campaign.scheduledAt)
        : dayjs().add(1, 'hour').set('minute', 0),
    },
    validationSchema,
    onSubmit: async (values) => {
      console.log(values);

      const channel = (values.channel.length > 1 ? 'all' : values.channel[0]) as CampaignChannelType;
      const scheduledAt = values.scheduledAt.toISOString();

      const recipientIds: number[] = [];
      const recipientGroups: string[] = [];
      values.recipients.forEach((recipient) => {
        if (recipient.category === 'group' && recipient.group) {
          recipientGroups.push(recipient.group);
        } else {
          recipientIds.push(recipient.id);
        }
      });

      const payload = {
        channel,
        scheduledAt,
        name: values.name,
        subject: values.subject,
        message: values.message,
        recipients: recipientIds,
        recipientGroups,
        status: 'queued',
      };
      try {
        if (campaign) {
          await updateCampaign({
            userId,
            campaign: {
              id: campaign.id,
              ...payload,
            },
          });
        } else {
          await addCampaign({
            userId,
            campaign: payload,
          });
        }
      } catch (error) {
        console.error('>>> Cannot submit campaign:', error);
      } finally {
        handleSave();
      }
    },
  });

  useEffect(() => {
    if (isSuccess && formik.dirty) {
      formik.resetForm();
    }
  }, [formik, isSuccess]);

  const selectedRecipientsCount = useMemo(() => {
    return formik.values.recipients.reduce((acc, curr) => acc + curr.count, 0)
      .toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }, [formik.values.recipients]);

  const isEditMode = Boolean(campaign);
  const formTitle = isEditMode ? 'Edit Message' : 'Create New Message';

  const getOptionLabel = (option: CampaignRecipientType) => {
    return option.category === 'user'
      ? option.name
      : `${option.name} (${option.count})`;
  };

  return (
    <CampaignFormContainer>
      <Typography variant="h5" gutterBottom>
        {formTitle}
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        <div className="input-box">
          <ChannelToggle
            value={formik.values.channel}
            onChange={(value) => {
              const valueSet = new Set(formik.values.channel);
              if (valueSet.has(value)) {
                valueSet.delete(value);
              } else {
                valueSet.add(value);
              }
              const valueArr = Array.from(valueSet);
              formik.setFieldTouched('channel', true);
              formik.setFieldValue('channel', valueArr, true);
            }}
            errorText={formik.errors.channel as string}
            isTouched={formik.touched.channel as unknown as boolean}
          />
        </div>
        <div className="input-box">
          <h3>Campaign Name</h3>
          <Input
            nameInput="name"
            onChangeValue={formik.handleChange}
            value={formik.values.name}
            errorText={formik.errors.name}
            isTouched={formik.touched.name}
            onBlur={formik.handleBlur}
          />
        </div>

        <div className="input-box">
          <h3>Recipient List</h3>
          <CustomAutocomplete
            multiple
            nameAutocomplete="recipients"
            value={formik.values.recipients}
            onChangeValue={(value) => formik.setFieldValue('recipients', value)}
            placeholder="Select recipients..."
            options={recipientList}
            groupBy={(option) => option.category}
            getOptionLabel={getOptionLabel}
            renderOption={(props, option) => {
              return (
                <Box component="li" {...props} key={option.id}>
                  {getOptionLabel(option)}
                  {(option as CampaignRecipientType).phone && (
                    <Phone sx={{ ml: 1 }} fontSize="small" />
                  )}
                </Box>
              );
            }}
            errorText={formik.errors.recipients as string || undefined}
            isTouched={formik.touched.recipients as unknown as boolean}
            onBlur={formik.handleBlur}
          />
        </div>

        <div className="input-box">
          <div className="recipient-list-count">
            <span className="recipient-list-count__number">{selectedRecipientsCount}</span> recipients selected
          </div>
        </div>

        <div className="input-box">
          <h3>Subject Line</h3>
          <Input
            nameInput="subject"
            onChangeValue={formik.handleChange}
            value={formik.values.subject}
            errorText={formik.errors.subject}
            isTouched={formik.touched.subject}
            onBlur={formik.handleBlur}
          />
        </div>

        <div className="input-box">
          <h3>Message Content</h3>
          <Button
            variant="contained"
            className="m10-0"
            onClick={async () => {
              if (formik.values.recipients.length !== 1) {
                formik.setFieldTouched('recipients', true, false);
                formik.setFieldError(
                  'recipients',
                  'Message can be generated only for one recipient',
                );
                return;
              }

              try {
                const res = await generateMessage({
                  subject: formik.values.subject,
                }).unwrap();

                formik.setFieldValue('message', res.message ?? res);
              } catch (err) {
                formik.setFieldError('message', 'Error generating message');
              }
            }}
          >
            Create message
          </Button>
          <CustomTextField
            nameTextField="message"
            value={formik.values.message}
            onChangeValue={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Write message content here..."
            rows={4}
            multiline
            errorText={formik.errors.message}
            isTouched={formik.touched.message}
          />
        </div>
        <div className="input-box">
          <h3>Schedule Date</h3>
          <CustomDatePicker
            value={dayjs(formik.values.scheduledAt)}
            label="mm/dd/yyyy"
            onChange={(value) => {
              if (!value) return;
              const newDate = formik.values.scheduledAt.clone().set('date', value.date());
              formik.setFieldTouched('scheduledAt', true);
              formik.setFieldValue('scheduledAt', newDate, true);
            }}
            errorText={formik.errors.scheduledAt as string || undefined}
            isTouched={formik.touched.scheduledAt as unknown as boolean}
            hideHelperText
          />
        </div>
        <div className="input-box">
          <h3>Send Time</h3>
          <CustomTimePicker
            value={dayjs(formik.values.scheduledAt)}
            onChange={(value) => {
              if (!value) return;
              const newDate = value.clone()
                .set('date', formik.values.scheduledAt.date());
              formik.setFieldValue('scheduledAt', newDate, true);
            }}
            errorText={formik.errors.scheduledAt as string || undefined}
            isTouched={formik.touched.scheduledAt as unknown as boolean}
          />
        </div>
        <Typography variant="subtitle2">
          Status: <strong>{campaign?.status}</strong>
        </Typography>
          {isEditMode ? (
            <div className="controls">
              { (campaign!.status !== 'deleted') && (
                <Button
                variant="outlined"
                disabled={formik.isSubmitting}
                onClick={async () => {
                  await updateStatus({ userId, id: campaign!.id, status: 'deleted' });
                  handleCancel();
                }}
                >
                  Delete
                </Button>)
              }

              { (campaign!.status === 'queued') && (
                <Button
                variant="outlined"
                disabled={formik.isSubmitting}
                onClick={async () => {
                  await updateStatus({ userId, id: campaign!.id, status: 'paused' });
                  handleCancel();
                }}
                >
                  Pause
                </Button>)
              }

              <Button
                variant="contained"
                type="submit"
                disabled={formik.isSubmitting}
                >
                  Update
              </Button>

              <Button
                variant="outlined"
                disabled={formik.isSubmitting}
                onClick={handleCancel}
                >
                  Cancel
              </Button>
            </div>
          ) : (
            <div className="controls">
              <Button
                variant="contained"
                type="submit"
                disabled={formik.isSubmitting}
                >
                  Shedule Message
              </Button>
            </div>
          )}

      </form>
    </CampaignFormContainer>
  );
};

export default CampaignForm;
